import React, { useState, useEffect } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Switch,
  TextField,
} from '@material-ui/core';
import { MilestoneIconAutocomplete } from './milestoneIcons';
import { Milestone } from './types';

interface EditMilestoneDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (milestone: Milestone) => void;
  milestone: Milestone | null;
}

interface MilestoneFormData {
  clientId: string;
  iconId?: string | null;
  isDisabled: boolean;
  name: string;
  label: string;
  order: number;
}

const emptyMilestone: MilestoneFormData = {
  clientId: '',
  iconId: undefined,
  name: '',
  label: '',
  isDisabled: false,
  order: 0,
};

export const EditMilestoneDialog: React.FC<EditMilestoneDialogProps> = ({ open, onClose, onSave, milestone }) => {
  const [formData, setFormData] = useState<MilestoneFormData>(emptyMilestone);

  useEffect(() => {
    if (milestone) {
      const milestoneIconId = (milestone as any).iconId as string | null | undefined;

      setFormData({
        clientId: milestone.clientId,
        iconId: milestoneIconId,
        isDisabled: !!milestone.isDisabled,
        name: milestone.name,
        label: milestone.label ?? milestone.name ?? '',
        order: milestone.order ?? 0,
      });
    } else {
      setFormData(emptyMilestone);
    }
  }, [milestone, open]);

  const handleChange = (name: keyof MilestoneFormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSave = () => {
    onSave({
      ...formData,
      id: milestone?.id || '',
      steps: milestone?.steps || [],
    });
    onClose();
  };

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={open}
      onClose={onClose}
    >
      <DialogTitle>
        {milestone ? 'Edit Milestone' : 'Create Milestone'}
      </DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          fullWidth
          label="Milestone Name"
          margin="normal"
          name="name"
          value={formData.name}
          onChange={(e) => handleChange('name', e.target.value)}
        />
        <TextField
          fullWidth
          label="Label"
          margin="normal"
          name="label"
          value={formData.label}
          onChange={(e) => handleChange('label', e.target.value)}
        />
        <TextField
          fullWidth
          label="Order"
          margin="normal"
          name="order"
          type="number"
          value={formData.order}
          onChange={(e) => handleChange('order', Number(e.target.value))}
        />

        <MilestoneIconAutocomplete
          value={formData.iconId}
          onChange={(iconId) => handleChange('iconId', iconId)}
        />

        <br />

        <FormControlLabel
          control={
            <Switch
              checked={formData.isDisabled}
              color="primary"
              name="isDisabled"
              onChange={(e) => handleChange('isDisabled', e.target.checked)}
            />
          }
          label="Is Disabled"
        />
      </DialogContent>
      <DialogActions>
        <Button
          color="primary"
          onClick={onClose}
        >
          Cancel
        </Button>
        <Button
          color="primary"
          variant="contained"
          onClick={handleSave}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};
